import { ValidationError } from 'components/ui/excel-table/types';
import { ECountries } from 'constants/enums';
import { TFunction } from 'i18next';
import { ContractResponseDtoContractStatusEnum } from 'libs/pinnacle-sdk';
import { isNil, keyBy } from 'lodash';
import {
  getGstRateOnCedingCommissionByReinsuredCountry,
  getGstRateOnPremiumByBrokerCountry,
} from 'utils';
import { isCancelledOrNTUContract } from '../validation-helper';

export enum EBinderScheduleKey {
  PREMIUM = 'premium',
  GST_PREMIUM = 'gst-premium',
  CEDING = 'ceding',
  GST_CEDING = 'gst-ceding',
  BROKERAGE = 'brokerage',
  GST_BROKERAGE = 'gst-brokerage',
  ERE = 'ere',
  WITHHOLDING_TAX = 'withholding-tax',
  OTHER_DEDUCTIONS = 'other-deductions',
  GST_ON_ERE = 'gst-on-ere',
  PINNACLE = 'pinnacle',
  AIT = 'ait',
  FEES = 'fees',
  REMITTANCE = 'remittance',
  NET_PREMIUM_DUE = 'net-premium-due',
  NET_PREMIUM_RECEIVED = 'net-premium-received',
}

export interface BinderScheduleItem {
  key: EBinderScheduleKey;
  label: string;
  rate?: number;
  amount?: number;
  fullAmount?: number;
  isNegative?: boolean;
  isEditable?: boolean;
  isBold?: boolean;
}

interface BinderSchedulePrefillData {
  premium: number;
  binderShareSigned?: number;
  brokerCountry?: string;
  reinsuredCountry?: string;
  layerTotalDeductions?: string;
  binderPinCommission?: string;
}

export const calculateBinderSchedule = (
  binderSchedule: BinderScheduleItem[],
  {
    premium,
    binderShareSigned = 0,
    brokerCountry,
    reinsuredCountry,
    layerTotalDeductions,
    binderPinCommission,
  }: BinderSchedulePrefillData,
  t: TFunction,
  allowPremiumRateEdit = false
): BinderScheduleItem[] => {
  const transformRates = keyBy(binderSchedule, 'key') as Record<
    EBinderScheduleKey,
    BinderScheduleItem
  >;
  const cedingCommissionRate =
    transformRates[EBinderScheduleKey.CEDING]?.rate ?? 0;
  const sourceBrokerageRate =
    transformRates[EBinderScheduleKey.BROKERAGE]?.rate ??
    (layerTotalDeductions ? Number(layerTotalDeductions) * 100 : 0);
  const ereRate = transformRates[EBinderScheduleKey.ERE]?.rate ?? 0;
  const withholdingTaxRate =
    transformRates[EBinderScheduleKey.WITHHOLDING_TAX]?.rate ?? 0;
  const otherDeductionsAmount =
    transformRates[EBinderScheduleKey.OTHER_DEDUCTIONS]?.amount ?? 0;
  const gstOnEreRate =
    transformRates[EBinderScheduleKey.GST_ON_ERE]?.rate ?? 10;
  const pinnacleCommissionRate = binderPinCommission
    ? Number(binderPinCommission)
    : 0;
  const aitRate =
    transformRates[EBinderScheduleKey.AIT]?.rate ??
    (reinsuredCountry === ECountries.AUSTRALIA ? 3 : 0);
  const feesAmount = transformRates[EBinderScheduleKey.FEES]?.amount ?? 0;
  const gstRateOnPremium = !isNil(
    transformRates[EBinderScheduleKey.GST_PREMIUM]?.rate
  )
    ? transformRates[EBinderScheduleKey.GST_PREMIUM]?.rate
    : getGstRateOnPremiumByBrokerCountry(brokerCountry);

  const gstRateOnCedingCommission =
    transformRates[EBinderScheduleKey.GST_CEDING]?.rate ??
    getGstRateOnCedingCommissionByReinsuredCountry(reinsuredCountry);
  const gstRateOnSourceBrokerage =
    transformRates[EBinderScheduleKey.GST_BROKERAGE]?.rate ??
    getGstRateOnPremiumByBrokerCountry(brokerCountry);
  const currentBinderShareSigned =
    transformRates[EBinderScheduleKey.PREMIUM]?.rate ?? binderShareSigned;
  // Calculate full amounts (based on 100% layer premium)
  const fullGstOnPremium = premium * (gstRateOnPremium / 100);
  const fullCedingCommission = premium * (cedingCommissionRate / 100);
  const fullGstOnCedingCommission =
    fullCedingCommission * (gstRateOnCedingCommission / 100);
  const fullSourceBrokerage = premium * (sourceBrokerageRate / 100);
  const fullGstOnSourceBrokerage =
    fullSourceBrokerage * (gstRateOnSourceBrokerage / 100);
  const fullEre = premium * (ereRate / 100);
  const fullGstOnEre = fullEre * (gstOnEreRate / 100);

  const fullWithholdingTax = premium * (withholdingTaxRate / 100);

  // Calculate share amounts by applying binder share percentage to full amounts
  const shareMultiplier = currentBinderShareSigned / 100;

  const fullOtherDeductions =
    premium < 0
      ? -Math.abs(otherDeductionsAmount) * (1 / shareMultiplier)
      : Math.abs(otherDeductionsAmount) * (1 / shareMultiplier);

  const fullNetPremiumDue =
    premium +
    fullGstOnPremium -
    fullCedingCommission -
    fullGstOnCedingCommission -
    fullSourceBrokerage -
    fullGstOnSourceBrokerage -
    fullEre -
    fullWithholdingTax -
    fullOtherDeductions -
    fullGstOnEre;

  const fullPinnacleCommission = premium * (pinnacleCommissionRate / 100);
  const fullAit = premium * (aitRate / 100);

  const fullFeeAmount =
    premium < 0
      ? -Math.abs(feesAmount) * (1 / shareMultiplier)
      : Math.abs(feesAmount) * (1 / shareMultiplier);

  const fullBinderCashRemittance =
    premium -
    fullEre -
    fullWithholdingTax -
    fullPinnacleCommission -
    fullAit -
    fullFeeAmount;

  const feeAmount = premium < 0 ? -Math.abs(feesAmount) : Math.abs(feesAmount);
  const otherDeductionsCalculated =
    premium < 0
      ? -Math.abs(otherDeductionsAmount)
      : Math.abs(otherDeductionsAmount);
  const sharePremium = premium * shareMultiplier;
  const gstOnPremium = fullGstOnPremium * shareMultiplier;
  const cedingCommission = fullCedingCommission * shareMultiplier;
  const gstOnCedingCommission = fullGstOnCedingCommission * shareMultiplier;
  const sourceBrokerage = fullSourceBrokerage * shareMultiplier;
  const gstOnSourceBrokerage = fullGstOnSourceBrokerage * shareMultiplier;
  const ere = fullEre * shareMultiplier;
  const gstOnEre = fullGstOnEre * shareMultiplier;
  const withholdingTax = fullWithholdingTax * shareMultiplier;
  const netPremiumDue = fullNetPremiumDue * shareMultiplier;
  const pinnacleCommission = fullPinnacleCommission * shareMultiplier;
  const ait = fullAit * shareMultiplier;
  const binderCashRemittance = fullBinderCashRemittance * shareMultiplier;

  return [
    {
      key: EBinderScheduleKey.PREMIUM,
      label: t('contractDetail.basePremium'),
      rate: currentBinderShareSigned,
      amount: sharePremium,
      fullAmount: premium,
      isEditable: allowPremiumRateEdit,
    },
    {
      key: EBinderScheduleKey.GST_PREMIUM,
      label: t('contractDetail.gstOnPremium'),
      rate: gstRateOnPremium,
      amount: gstOnPremium,
      fullAmount: fullGstOnPremium,
      isEditable: allowPremiumRateEdit,
    },
    {
      key: EBinderScheduleKey.CEDING,
      label: t('contractDetail.cedingCommission'),
      rate: cedingCommissionRate,
      amount: cedingCommission,
      fullAmount: fullCedingCommission,
      isNegative: true,
      isEditable: true,
    },
    {
      key: EBinderScheduleKey.GST_CEDING,
      label: t('contractDetail.gstOnCedingCommission'),
      rate: gstRateOnCedingCommission,
      amount: gstOnCedingCommission,
      fullAmount: fullGstOnCedingCommission,
      isNegative: true,
      isEditable: true,
    },
    {
      key: EBinderScheduleKey.BROKERAGE,
      label: t('contractDetail.sourceBrokerage'),
      rate: sourceBrokerageRate,
      amount: sourceBrokerage,
      fullAmount: fullSourceBrokerage,
      isNegative: true,
      isEditable: true,
    },
    {
      key: EBinderScheduleKey.GST_BROKERAGE,
      label: t('contractDetail.gstOnSourceBrokerage'),
      rate: gstRateOnSourceBrokerage,
      amount: gstOnSourceBrokerage,
      fullAmount: fullGstOnSourceBrokerage,
      isNegative: true,
      isEditable: true,
    },
    {
      key: EBinderScheduleKey.ERE,
      label: t('contractDetail.ere'),
      rate: ereRate,
      amount: ere,
      fullAmount: fullEre,
      isNegative: true,
      isEditable: true,
    },
    {
      key: EBinderScheduleKey.WITHHOLDING_TAX,
      label: t('contractDetail.withholdingTax'),
      rate: withholdingTaxRate,
      amount: withholdingTax,
      fullAmount: fullWithholdingTax,
      isNegative: true,
      isEditable: true,
    },
    {
      key: EBinderScheduleKey.OTHER_DEDUCTIONS,
      label: t('contractDetail.otherDeductions'),
      amount: otherDeductionsCalculated,
      fullAmount: fullOtherDeductions,
      isNegative: true,
      isEditable: true,
    },
    {
      key: EBinderScheduleKey.GST_ON_ERE,
      label: t('contractDetail.gstOnEre'),
      rate: gstOnEreRate,
      amount: gstOnEre,
      fullAmount: fullGstOnEre,
      isNegative: true,
      isEditable: true,
    },
    {
      key: EBinderScheduleKey.NET_PREMIUM_DUE,
      label: t('contractDetail.netPremiumDueFromBroker'),
      amount: netPremiumDue,
      fullAmount: fullNetPremiumDue,
      isBold: true,
    },
    {
      // TODO: Will get updated when the finance steps are carried out
      key: EBinderScheduleKey.NET_PREMIUM_RECEIVED,
      label: t('contractDetail.netPremiumReceivedFromBroker'),
      amount: netPremiumDue,
      fullAmount: fullNetPremiumDue,
      isBold: true,
    },
    {
      key: EBinderScheduleKey.PINNACLE,
      label: t('contractDetail.pinnacleCommission'),
      rate: pinnacleCommissionRate,
      amount: pinnacleCommission,
      fullAmount: fullPinnacleCommission,
      isNegative: true,
    },
    {
      key: EBinderScheduleKey.AIT,
      label: t('contractDetail.ait'),
      rate: aitRate,
      amount: ait,
      fullAmount: fullAit,
      isNegative: true,
      isEditable: true,
    },
    {
      key: EBinderScheduleKey.FEES,
      // TODO: Taxes and fees need to be removed in the future as we are no longer using it
      // Discussion thread: https://linear.app/pinnacle-uw/issue/PIN-912/premium-processing-taxes-and-fees-should-be-negative-when-creating#comment-e2b52a4f
      label: t('contractDetail.taxesAndFees'),
      amount: feeAmount,
      fullAmount: fullFeeAmount,
      isNegative: true,
      isEditable: true,
    },
    {
      key: EBinderScheduleKey.REMITTANCE,
      label: t('contractDetail.binderCashRemittance'),
      amount: binderCashRemittance,
      fullAmount: fullBinderCashRemittance,
      isBold: true,
    },
  ];
};

export const validateBinderSchedule =
  (contractStatus?: ContractResponseDtoContractStatusEnum) =>
  (
    row: BinderScheduleItem,
    rowIndex: number,
    _: BinderScheduleItem[],
    t: TFunction
  ) => {
    const errors: ValidationError<BinderScheduleItem>[] = [];

    if (isCancelledOrNTUContract(contractStatus)) {
      return errors;
    }

    // not any rate can be negative
    if (row.rate && row.rate < 0) {
      errors.push({
        field: 'rate',
        message: t('general.nonNegative', { field: row.label }),
        rowIndex,
      });
    }

    if (row.key === EBinderScheduleKey.PREMIUM && row.rate === 0) {
      errors.push({
        field: 'rate',
        message: t('general.notZero', { field: row.label }),
        rowIndex,
      });
    }
    return errors;
  };
